import { api } from "./apiSlice";
import { showMessage } from "../../helpers";

export const adminPartnerApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getPartnerList: builder.query({
      query: (params = {}) => {
        const { limit = 10, offset = 0, company_name, list = false, ordering } = params;

        const searchParams = new URLSearchParams();
        searchParams.append('limit', limit);
        searchParams.append('offset', offset);
          if (company_name) {
            searchParams.append('company_name', company_name);
          }
          if (list) {
            searchParams.append('list', list);
          }
          if (ordering) {
            searchParams.append('ordering', ordering);
          }

        return `/admin/partner/?${searchParams.toString()}`;
      },
      providesTags: ["AdminPartner"],
    }),

    getSinglePartner: builder.query({
      query: (id) => ({
        url: `/admin/partner/${id}/`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{ type: "AdminPartner", id }],
    }),

    addPartner: builder.mutation({
      query: (data) => ({
        url: "/admin/partner/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["AdminPartner"],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          showMessage("success", "Partner added successfully!");
        } catch (error) {
          showMessage("error", error.toString());
        }
      },
    }),

    editPartner: builder.mutation({
      query: ({ id, data }) => ({
        url: `/admin/partner/${id}/`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        "AdminPartner",
        { type: "AdminPartner", id },
      ],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          showMessage("success", "Partner edited successfully!");
        } catch (error) {
          showMessage("error", error.toString());
        }
      },
    }),

    deletePartner: builder.mutation({
      query: (id) => ({
        url: `/admin/partner/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["AdminPartner"],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          showMessage("success", "Partner deleted successfully!");
        } catch (error) {
          showMessage("error", error.toString());
        }
      },
    }),

    getLicensorList: builder.query({
      query: (params = {}) => {
        const { limit = 100, offset = 0 } = params;
        const searchParams = new URLSearchParams();
        searchParams.append("limit", limit);
        searchParams.append("offset", offset);
        return `/admin/licensor/?${searchParams.toString()}`;
      },
      providesTags: ["AdminLicensor"],
    }),

    addLicensor: builder.mutation({
      query: (data) => ({
        url: "/admin/licensor/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["AdminLicensor"],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          showMessage("success", "Licensor added successfully!");
        } catch (error) {
          showMessage("error", error.toString());
        }
      },
    }),

    updateLicensor: builder.mutation({
      query: ({ id, data }) => ({
        url: `/admin/licensor/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["AdminLicensor"],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          showMessage("success", "Licensor updated successfully!");
        } catch (error) {
          showMessage("error", error.toString());
        }
      },
    }),

    deleteLicensor: builder.mutation({
      query: (id) => ({
        url: `/admin/licensor/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["AdminLicensor"],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          showMessage("success", "Licensor deleted successfully!");
        } catch (error) {
          showMessage("error", error.toString());
        }
      },
    }),

    getCountryList: builder.query({
      query: () => ({
        url: "/country_list/?is_detailed=true",
        method: "GET",
      }),
      providesTags: ["Countries"],
    }),

    getCountryListDiagnostic: builder.query({
      query: () => ({
        url: "/country_list_diagnostic/?is_detailed=true",
        method: "GET",
      }),
      providesTags: ["Countries"],
    }),

    getCutOffDate: builder.query({
      query: () => "/compliance/cutoff-date/",
      providesTags: ["CutOffDate"],
    }),

    setCutOffDate: builder.mutation({
      query: (data) => ({
        url: "/compliance/cutoff-date/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["CutOffDate"],
    }),

    getLetterDate: builder.query({
      query: () => "/compliance/letter-submission-date/",
      providesTags: ["LetterSubmissionDate"],
    }),

    setLetterDate: builder.mutation({
      query: (data) => ({
        url: "/compliance/letter-submission-date/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["LetterSubmissionDate"],
    }),

    sendReminder: builder.mutation({
      query: (data) => ({
        url: "/template/reminder/",
        method: "POST",
        body: data,
      }),
    }),

    // Tax Invoice related endpoints
    getLicensorsPartnersList: builder.query({
      query: () => ({
        url: "/licensors-partner/",
        method: "GET",
      }),
      providesTags: ["LicensorsPartners"],
    }),

    getSingleTaxRequest: builder.query({
      query: (id) => ({
        url: `/partner/royalty-tax-invoice/${id}/`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{ type: "TaxRequest", id }],
    }),

    addTaxInvoiceRequest: builder.mutation({
      query: (data) => ({
        url: "/partner/royalty-tax-invoice/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["TaxRequest"],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          showMessage("success", "Tax document added successfully!");
        } catch (error) {
          showMessage("error", "Failed to save tax document");
        }
      },
    }),

    editTaxInvoiceRequest: builder.mutation({
      query: ({ id, data }) => ({
        url: `/partner/royalty-tax-invoice/${id}/`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        "TaxRequest",
        { type: "TaxRequest", id },
      ],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          showMessage("success", "Tax document updated successfully!");
        } catch (error) {
          showMessage("error", "Failed to save tax document");
        }
      },
    }),

    deleteInvoiceRequest: builder.mutation({
      query: (id) => ({
        url: `/partner/royalty-invoice-tracking/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["TaxRequest"],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          showMessage("success", "Removed!");
        } catch (error) {
          showMessage("error", "Failed to delete");
        }
      },
    }),
  }),
});

export const {
  useGetPartnerListQuery,
  useGetSinglePartnerQuery,
  useAddPartnerMutation,
  useEditPartnerMutation,
  useDeletePartnerMutation,
  useGetLicensorListQuery,
  useAddLicensorMutation,
  useUpdateLicensorMutation,
  useDeleteLicensorMutation,
  useGetCountryListQuery,
  useGetCountryListDiagnosticQuery,
  useGetCutOffDateQuery,
  useSetCutOffDateMutation,
  useGetLetterDateQuery,
  useSetLetterDateMutation,
  useSendReminderMutation,
  useGetLicensorsPartnersListQuery,
  useGetSingleTaxRequestQuery,
  useAddTaxInvoiceRequestMutation,
  useEditTaxInvoiceRequestMutation,
  useDeleteInvoiceRequestMutation,
} = adminPartnerApi;
